import {
  type ExamCenterProctor,
  type StorageObject,
  useGetDownloadSignedUrlsForExamCenterProctorQuery,
} from '@exam-center/graphql';
import { getContentTypeFromFetchResponse, getModeByFileType } from '@exam-center/utils';
import { useEffect, useMemo, useState } from 'react';

import { type Proctor } from '../../providers';

interface UseGetStorageObjectsFileProps {
  proctor: Proctor;
}

function useGetStorageObjectsFile(
  storageObjectsKey: keyof ExamCenterProctor,
  params: Readonly<UseGetStorageObjectsFileProps>,
) {
  const { proctor } = params;
  const [files, setFiles] = useState<
    Array<{
      name: string;
      mode?: 'preview' | 'download';
      value: {
        url: string;
        name: string;
      };
    }>
  >([]);

  const shouldSkip = !proctor?.id;

  useEffect(() => {
    if (shouldSkip) {
      setFiles([]);
      return;
    }
  }, [shouldSkip]);

  const storageObjects = proctor?.[storageObjectsKey] as Array<StorageObject> | undefined;
  const fileNames = useMemo(
    () => storageObjects?.map((file) => file.key.split('/').pop() ?? '') ?? [],
    [storageObjects],
  );

  const { data } = useGetDownloadSignedUrlsForExamCenterProctorQuery({
    variables: {
      getDownloadSignedUrlsForExamCenterProctorInput: {
        examCenterProctorId: proctor?.id ?? '',
        fileNames,
      },
    },
    skip: shouldSkip,
  });

  useEffect(() => {
    if (!storageObjects || fileNames.length === 0) {
      setFiles([]);
      return;
    }
    const getFiles = async () => {
      const files = await Promise.all(
        storageObjects.map(async (file, idx) => {
          const fileName = fileNames[idx];
          const url = data?.ecGetDownloadSignedUrlsForExamCenterProctor.signedUrls[fileName];
          const type = (await getContentTypeFromFetchResponse(url)) ?? '';

          return {
            name: file.fileName ?? '-',
            mode: getModeByFileType(type),
            value: {
              url: url ?? '',
              name: fileName ?? '',
            },
          } as const;
        }),
      );
      setFiles(files);
    };
    getFiles();
  }, [storageObjects, fileNames, data]);

  return files;
}

export function useGetAgreementStorageObjectsFile(params: Readonly<UseGetStorageObjectsFileProps>) {
  return useGetStorageObjectsFile('agreementStorageObjects', params);
}
